import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Form, Input, Row, InputNumber, Tabs, message, DatePicker, Select } from 'antd';
import { usePageTitle } from '../../../context/pageTitleContext';
import { dateFormat, menuLinks, selectOptionsTypes } from '../../../utils/constants';
import { SaveOutlined, RollbackOutlined } from '@ant-design/icons';
import Loader from '../../../components/common/Loader';
import { useLoader } from '../../../context/loaderContext';
import { useNavigate, useParams } from 'react-router';

import dayjs from 'dayjs';

import CustomerOrdersServices from '../../../services/customerOrdersServices';
import { Can, permissions } from '../../../utils/permissionUtils';
import { useUserAuth } from '../../../context/UserContext';
import DynamicSelect from '../../../components/common/dynamicSelect';
import { useSelector } from 'react-redux';
import { handleDateConvertForApis } from '../../../utils/functions';
import CreditNotesServices from '../../../services/creditNotesServices';
import CreditNoteItems from './creditNoteItems';


const CreditNoteDetails = () => {
    const { t } = useTranslation();
    const { loading, setLoading, setHasError } = useLoader();
    const salesOrganization = useSelector((state: any) => state.utils.salesOrganization);

    const [form] = Form.useForm();
    const urlParams = useParams();
    const { user } = useUserAuth();

    const { setPageTitle } = usePageTitle();

    const [creditNoteData, setCreditNoteData] = useState(null);
    const [creditNoteItemsData , setCreditNoteItemsData] = useState([]);

    const [openUpdateDrawer, setOpenUpdateDrawer] = useState(false);
    const [openInsertDrawer, setOpenInsertDrawer] = useState(false);
    const [selectedOrderItem, setSelectedOrderItem] = useState(null);
    const [activeTab, setActiveTab] = useState("1");

    const [shipToObjectData, setShipToObjectData] = useState(null);
    const [shipToObjectDataLoading, setShipToObjectDataLoading] = useState(false);
    const [soldToObjectData, setSoldToObjectData] = useState(null);
    const [soldToObjectDataLoading, setSoldToObjectDataLoading] = useState(false);


    const navigate = useNavigate();

    useEffect(() => {
        setPageTitle(t('page.creditNoteDetails'));
        getCreditNoteDetails();
    }, []);

    const getCreditNoteDetails = async () => {
        setLoading(true);
        const payload = {
            headerID: urlParams.id,
            userID: user.id
        }
        
        try {
            const respData = await CreditNotesServices.getCreditNoteDetails(payload);
            if (respData.success) {
                let result = respData.responseObject.value.creditNotesDetailsDto[0];
                
                setCreditNoteItemsData(result.creditNotesDetailsItems);
                setCreditNoteData(result.creditNotesDetailsHeader);            
            } else {
                message.error(t('messages.errorAccured'));
                setHasError(true);
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
            setHasError(true);
        }
        finally {
            setLoading(false);
        }
    }

    const onOrderSave = async () => {

        try {
            const values = await form.validateFields();
            values.orderDate = handleDateConvertForApis(values.orderDate);
            values.requestedDeliveryDate = handleDateConvertForApis(values.requestedDeliveryDate);

            console.log("order update values", values);


        } catch (errorInfo) {
            console.log('Validation Failed:', errorInfo);
            message.error(t('messages.formValidationError'));
        }


    }


    const CreditNoteHeader = ({ creditNoteData }: { creditNoteData: any }) => {
        return (
            <>
                <Form
                    layout={"vertical"}
                    form={form}
                    size='small'
                    className='orderData orderFields'
                    disabled
                >
                    <Row gutter={[16, 16]}>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} >
                            <Card title="Order Details" size='small'>
                                <Row gutter={[16, 24]}>
                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.orderNumber')}
                                            name="orderNumber"
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                            initialValue={creditNoteData.orderNumber}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.orderType')}
                                            name="orderType"
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                            initialValue={creditNoteData.orderType}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.orderType} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.orderDate')}
                                            name="orderDate"
                                            initialValue={creditNoteData.orderDate && dayjs(creditNoteData?.orderDate)}
                                        >
                                            <DatePicker style={{ width: "100%" }} format={dateFormat} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.totalAmount')}
                                            name="totalAmount"
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                            initialValue={creditNoteData.totalAmount}
                                        >
                                            <InputNumber style={{ width: "100%" }} min={0} precision={2} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.billToParty')}
                                            name="billToParty"
                                            initialValue={creditNoteData.billToParty}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.billToParty} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.shipTo')}
                                            name="shipToParty"
                                            initialValue={creditNoteData.shipToParty}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.shipTo} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>


                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.soldTo')}
                                            name="soldToParty"
                                            initialValue={creditNoteData.soldToParty}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.soldTo} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.billingBlock')}
                                            name="billingBlock"
                                            initialValue={creditNoteData.billingBlock}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.billingBlock} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.deliveryBlock')}
                                            name="deliveryBlock"
                                            initialValue={creditNoteData.deliveryBlock}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.deliveryBlock} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.division')}
                                            name="division"
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                            initialValue={creditNoteData.division}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.division} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.orderReason')}
                                            name="orderReason"
                                            initialValue={creditNoteData.orderReason}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.orderReason} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.payer')}
                                            name="payer"
                                            initialValue={creditNoteData.payer}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.paymentTerms')}
                                            name="paymentTerms"
                                            initialValue={creditNoteData.paymentTerms}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.paymentTerms} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>
                                    </Col>


                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.poNumber')}
                                            name="poNumber"
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                            initialValue={creditNoteData.poNumber}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>


                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.quotationNumber')}
                                            name="quotationNumber"
                                            initialValue={creditNoteData.quotationNumber}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>


                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.reqDeliveryDate')}
                                            name="requestedDeliveryDate"
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                            initialValue={dayjs(creditNoteData?.requestedDeliveryDate)}

                                        >
                                            <DatePicker style={{ width: "100%" }} format={dateFormat} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >

                                        <Form.Item
                                            label={t('order.salesOrganization')}
                                            name="salesOrganization"
                                                initialValue={creditNoteData.salesOrganizationID}
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                        >
                                            <DynamicSelect location="update" type={selectOptionsTypes.salesOrganization} value={null} onChange={null} onSelectChange={null} />
                                        </Form.Item>

                                        {/* <Form.Item
                                            label={t('order.salesOrganization')}
                                            name="salesOrganization"
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                            initialValue={orderHeaderData.salesOrganizationID}
                                        >
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                placeholder={t('general.select')}
                                                options={salesOrganization.map((item) => ({ value: item.id, label: item.code }))}
                                                onChange={(value, option) => onSelectOptionChange('salesOrganization', option)}
                                            />
                                        </Form.Item> */}
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                        <Form.Item
                                            label={t('order.distributionChannel')}
                                            name="distributionChannel"
                                            rules={[{ required: true, message: t('messages.requiredField') }]}
                                            initialValue={creditNoteData.distributionChannel}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>

                                </Row>
                            </Card>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }}>
                            <Row gutter={[16, 16]}>

                                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} >
                                    <Card title={t('order.customerSoldTo')} size='small'>
                                        <Row gutter={[16, 16]}>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.name')}
                                                    name="customerName"
                                                    initialValue={creditNoteData.customerName}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.email')}
                                                    name="customerEmail"
                                                    initialValue={creditNoteData.customerEmail}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.mobilePhone')}
                                                    name="customerMobilePhone"
                                                    initialValue={creditNoteData.customerMobilePhone}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.telephone')}
                                                    name="customerTelephone"
                                                    initialValue={creditNoteData.customerTelephone}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.city')}
                                                    name="customerCity"
                                                    initialValue={creditNoteData.customerCity}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.region')}
                                                    name="customerRegion"
                                                    initialValue={creditNoteData.customerRegion}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.postalCode')}
                                                    name="customerPostalCode"
                                                    initialValue={creditNoteData.customerPostalCode}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.streetAddress')}
                                                    name="customerStreetAddress"
                                                    initialValue={creditNoteData.customerStreetAddress}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.taxNumber') + " 1"}
                                                    name="customerTaxNumber1"
                                                    initialValue={creditNoteData.customerTaxNumber1}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.taxNumber') + " 2"}
                                                    name="customerTaxNumber2"
                                                    initialValue={creditNoteData.customerTaxNumber2}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.taxNumber') + " 4"}
                                                    name="customerTaxNumber4"
                                                    initialValue={creditNoteData.customerTaxNumber4}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>

                                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} >
                                    <Card title={t('order.shipTo')} size='small'>
                                        <Row gutter={[16, 16]}>
                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.name')}
                                                    name="contactFullName"
                                                    initialValue={creditNoteData.contactFullName}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.email')}
                                                    name="contactEmail"
                                                    initialValue={creditNoteData.contactEmail}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.mobile')}
                                                    name="contactMobilePhone"
                                                    initialValue={creditNoteData.contactMobilePhone}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.telephone')}
                                                    name="contactTelephone"
                                                    initialValue={creditNoteData.contactTelephone}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.city')}
                                                    name="contactCity"
                                                    initialValue={creditNoteData.contactCity}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.region')}
                                                    name="contactRegion"
                                                    initialValue={creditNoteData.contactRegion}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.postalCode')}
                                                    name="contactPostalCode"
                                                    initialValue={creditNoteData.contactPostalCode}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.streetAddress')}
                                                    name="contactStreetAddress"
                                                    initialValue={creditNoteData.contactStreetAddress}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.taxNumber') + " 1"}
                                                    name="contactTaxNumber1"
                                                    initialValue={creditNoteData.contactTaxNumber1}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.taxNumber') + " 2"}
                                                    name="contactTaxNumber2"
                                                    initialValue={creditNoteData.contactTaxNumber2}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} >
                                                <Form.Item
                                                    label={t('order.taxNumber') + " 4"}
                                                    name="contactTaxNumber4"
                                                    initialValue={creditNoteData.contactTaxNumber4}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Form>
            </>
        )
    }


    const items = [
        {
            key: '1',
            label: t('creditNote.creditNote'),
            children: <CreditNoteHeader creditNoteData={creditNoteData} />,
        },
        {
            key: '2',
            label: t('creditNote.creditNoteItems'),
            children: <CreditNoteItems rowsData={creditNoteItemsData} />,
        }
    ];

    if (loading) { return (<Loader />) }

    return (

        <div className="customerOrderDetails">

            {creditNoteData && (
                <Card>
                    <Tabs
                        defaultActiveKey={activeTab}
                        onChange={(activeKey) => { setActiveTab(activeKey) }}
                        items={items}
                        tabBarExtraContent={
                            <>
                                <Button size='small' onClick={() => navigate(menuLinks.creditNote)} icon={<RollbackOutlined />} type="default">{t('creditNote.returnToCreditNote')}</Button>
                                {/* {activeTab === "1" && (
                                    <Can permission={permissions.UPDATE_ORDER}>
                                        <Button onClick={onOrderSave} icon={<SaveOutlined />} className='ml10' size='small' type="primary" htmlType='submit'>{t('general.save')}</Button>
                                    </Can>
                                )} */}
                            </>
                        } />
                </Card>
            )}

        </div>

    );
}
export default CreditNoteDetails;